// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

/**
 * Address Configuration for International Address Support
 *
 * Supports ISO 19160 international addressing standards for:
 * - Ireland (IRL) - Eircode format
 * - United Kingdom (GBR) - UK Postcode format
 */

export interface CountryConfig {
  code: string;
  name: string;
  postalCodeLabel: string;
  postalCodePlaceholder: string;
  postalCodePattern: RegExp;
  postalCodeFormat: string;
  stateProvinceLabel: string;
  stateProvincePlaceholder: string;
  stateProvinceRequired: boolean;
  townCityLabel: string;
  townCityPlaceholder: string;
}

export const COUNTRY_CONFIGS: Record<string, CountryConfig> = {
  IRL: {
    code: 'IRL',
    name: 'Ireland',
    postalCodeLabel: 'Eircode',
    postalCodePlaceholder: 'D04 XY45',
    postalCodePattern: /^[A-Z]\d{2}\s?[A-Z0-9]{4}$|^D6W\s?[A-Z0-9]{4}$/i,
    postalCodeFormat: 'XXX XXXX (e.g., D04 XY45)',
    stateProvinceLabel: 'County',
    stateProvincePlaceholder: 'County Dublin',
    stateProvinceRequired: false,
    townCityLabel: 'Town/City',
    townCityPlaceholder: 'Dublin 4',
  },
  GBR: {
    code: 'GBR',
    name: 'United Kingdom',
    postalCodeLabel: 'Post code',
    postalCodePlaceholder: 'SW3 4UT',
    postalCodePattern: /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i,
    postalCodeFormat: 'XX# #XX (e.g., SW3 4UT)',
    stateProvinceLabel: 'County',
    stateProvincePlaceholder: 'Greater London',
    stateProvinceRequired: false,
    townCityLabel: 'City',
    townCityPlaceholder: 'London',
  },
};

export const DEFAULT_COUNTRY = 'IRL';

export const SUPPORTED_COUNTRIES = [
  { key: 'IRL', value: 'IRL', text: 'Ireland', flag: 'ie' },
  { key: 'GBR', value: 'GBR', text: 'United Kingdom', flag: 'gb' },
];

/**
 * Get country configuration for a specific country code
 */
export function getCountryConfig(countryCode: string): CountryConfig {
  return COUNTRY_CONFIGS[countryCode] || COUNTRY_CONFIGS[DEFAULT_COUNTRY];
}

/**
 * Normalize postal code format for a specific country
 */
export function normalizePostalCode(countryCode: string, postalCode: string): string {
  if (!postalCode) return '';

  const cleaned = postalCode.replace(/\s+/g, '').toUpperCase();

  switch (countryCode) {
    case 'IRL':
      // Irish Eircode: XXX XXXX (e.g., D04 XY45)
      if (cleaned.length === 7) {
        return `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`;
      }
      return cleaned;

    case 'GBR':
      // UK Postcode: varies, but always has space before last 3 chars (e.g., SW3 4UT)
      if (cleaned.length >= 5) {
        return `${cleaned.slice(0, -3)} ${cleaned.slice(-3)}`;
      }
      return cleaned;

    default:
      return postalCode;
  }
}

/**
 * Validate postal code for a specific country
 */
export function validatePostalCode(countryCode: string, postalCode: string): boolean {
  if (!postalCode) return false;

  const config = getCountryConfig(countryCode);
  return config.postalCodePattern.test(postalCode);
}
